import React, {
  forwardRef,
  memo,
  useEffect,
  useImperativeHandle,
  useRef,
} from "react";
import {
  Accordion,
  AccordionButton,
  AccordionIcon,
  AccordionItem,
  AccordionPanel,
  Box,
  Flex,
  Text,
  useDisclosure,
} from "@chakra-ui/react";
import { WrapBridgeMappings } from "@constants/common";
import BaseImage from "@components/BaseImage";
import { BW_VARIANT, TBW_VARIANT } from "./types";

const learnMoreRsEth = {
  label: "Learn more about using rsETH across chains",
  desc: "",
  faqs: [
    {
      question: "What is bridging?  ",
      answer:
        "Bridging is the process of moving tokens from one blockchain to another.",
    },
    {
      question: "What are wrapped assets?",
      answer:
        "Wrapped tokens are digital assets that mirror the value of another cryptocurrency from a different blockchain. They are created to enhance interoperability between different blockchains, allowing assets from one blockchain to be used on another.",
    },
    {
      question: "What is wrsETH?",
      answer:
        "wrsETH is the wrapped token for rsETH, Kelp’s Liquid Staked Token. wrsETH is use on L2s where rsETH is not available natively.",
    },
    {
      question: "Where can I use wrsETH?",
      answer: "wrsETH is currently live on 8 L2 networks.",
      supported: [
        {
          chain: "Linea",
          icon: WrapBridgeMappings.ICONS[59144],
        },
        {
          chain: "Optimism",
          icon: WrapBridgeMappings.ICONS[10],
        },
        {
          chain: "Scroll",
          icon: WrapBridgeMappings.ICONS[534352],
        },
        {
          chain: "ZKSync",
          icon: WrapBridgeMappings.ICONS[324],
        },
        {
          chain: "Blast",
          icon: WrapBridgeMappings.ICONS[81457],
        },
        {
          chain: "Mode",
          icon: WrapBridgeMappings.ICONS[34443],
        },
        {
          chain: "Base",
          icon: WrapBridgeMappings.ICONS[8453],
        },
        {
          chain: "Manta Pacific",
          icon: WrapBridgeMappings.ICONS[169],
        },
      ],
    },
    {
      question: "Where can I use rsETH?",
      answer: "rsETH is currently live on Ethereum & Arbitrum.",
      supported: [
        {
          chain: "Ethereum",
          icon: WrapBridgeMappings.ICONS[1],
        },
        {
          chain: "Arbitrum",
          icon: WrapBridgeMappings.ICONS[42161],
        },
      ],
    },
  ],
};

const learnMoreAgEth = {
  label: "Learn more about using agETH across chains",
  desc: "",
  faqs: [
    {
      question: "What is bridging?",
      answer:
        "Bridging is the process of moving tokens from one blockchain to another.",
    },
    {
      question: "What are wrapped assets?",
      answer:
        "Wrapped tokens are digital assets that mirror the value of another cryptocurrency from a different blockchain. They are created to enhance interoperability between different blockchains, allowing assets from one blockchain to be used on another.",
    },
    {
      question: "What are agETH & agETH.axl?",
      answer:
        "agETH is the receipt token for Gain's Airdrop Vault. agETH.axl is the wrapped token for agETH, used on L2s where agETH is not yet available.",
    },
    {
      question: "Where can I use agETH.axl?",
      answer: "agETH.axl is currently live on three L2 networks.",
      supported: [
        {
          chain: "Linea",
          icon: WrapBridgeMappings.ICONS[59144],
        },
        {
          chain: "Scroll",
          icon: WrapBridgeMappings.ICONS[534352],
        },
        {
          chain: "Arbitrum",
          icon: WrapBridgeMappings.ICONS[42161],
        },
      ],
    },
  ],
};

type TLearnMore = {
  variant?: TBW_VARIANT;
};

const LearnMore = forwardRef((props: TLearnMore, ref) => {
  const { variant } = props;
  const { isOpen, onOpen, onClose } = useDisclosure();
  const accordionRef = useRef<HTMLDivElement>(null);

  const handleLinkClick = () => {
    onOpen();
    accordionRef?.current?.scrollIntoView({ behavior: "smooth" });
  };

  useEffect(() => {
    if (isOpen) {
      setTimeout(() => {
        accordionRef.current?.scrollIntoView({
          behavior: "smooth",
        });
      }, 300);
    }
  }, [isOpen]);

  useImperativeHandle(ref, () => ({
    openAccordion: () => handleLinkClick(),
  }));

  const learnMoreData =
    variant === BW_VARIANT.AGETH ? learnMoreAgEth : learnMoreRsEth;
  return (
    <Accordion
      allowToggle
      w="100%"
      display="flex"
      flexDirection="column"
      index={isOpen ? 0 : -1}
    >
      <AccordionItem border={"none"} ref={accordionRef}>
        <AccordionButton
          px="0"
          _hover={{
            borderRadius: { base: "16px", lg: "24px" },
          }}
          onClick={isOpen ? onClose : onOpen}
        >
          <Box as="span" flex="1" textAlign="left">
            <Flex gap={1}>
              <Text fontWeight="400" fontSize="12px">
                {learnMoreData.label}
              </Text>
            </Flex>
          </Box>
          <AccordionIcon />
        </AccordionButton>

        <AccordionPanel pb={{ base: "12px", lg: "1.5rem " }} px="0">
          <Flex>
            {learnMoreData.faqs.length > 0 && (
              <Flex direction={"column"} gap="20px">
                {learnMoreData.faqs.map((v, idx) => {
                  return (
                    <Flex key={`lm_${idx}`} direction={"column"} gap="10px">
                      <Text
                        color="textPrimary"
                        fontWeight="600"
                        fontSize="14px"
                        lineHeight="21px"
                      >
                        {v.question}
                      </Text>
                      <Text
                        color="textSecondary"
                        fontWeight="400"
                        fontSize="14px"
                        lineHeight="21px"
                      >
                        {v.answer}
                      </Text>
                      {v.supported && v.supported.length > 0 && (
                        <Flex gap="10px" wrap={"wrap"}>
                          {v.supported.map((c, index) => {
                            return (
                              <Flex
                                key={`faq_${index}`}
                                border="1px solid #638E8E"
                                borderRadius={"32px"}
                                p={"4px 6px 4px 4px"}
                                gap="4px"
                              >
                                <BaseImage
                                  src={c.icon}
                                  alt="swap"
                                  height={20}
                                  width={20}
                                  loading="eager"
                                  style={{ borderRadius: "50%" }}
                                />
                                <Text fontWeight="500" fontSize="14px" pt="1px">
                                  {c.chain}
                                </Text>
                              </Flex>
                            );
                          })}
                        </Flex>
                      )}
                    </Flex>
                  );
                })}
              </Flex>
            )}
          </Flex>
        </AccordionPanel>
      </AccordionItem>
    </Accordion>
  );
});

LearnMore.displayName = "LearnMore";

export default memo(LearnMore);
